import { AlertTriangle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { advisoryForLine, type SpecLine } from './specAdvisories';

interface SpecAdvisoryRowChipProps {
  line: SpecLine;
  /** Apply this row's recommended fix. */
  onApply: () => void;
  disabled?: boolean;
  className?: string;
}

// Per-row spec-advisory chip (86d3hk6pg). Renders an amber "fix-it" pill in the Line Items
// status cell when THIS line violates a sales spec rule, so a user scrolling a long order
// can spot and fix the offending rows individually. Clicking applies the same one-click fix
// as the aggregate toolbar badge, scoped to this line. Renders nothing on a clean line.
export function SpecAdvisoryRowChip({
  line,
  onApply,
  disabled,
  className,
}: SpecAdvisoryRowChipProps) {
  const rule = advisoryForLine(line);
  if (!rule) return null;

  const aria = `${rule.description} Click to apply to this line.`;
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onApply}
            disabled={disabled}
            aria-label={aria}
            className={cn(
              'inline-flex h-6 items-center gap-1 rounded border border-warning/40 bg-warning/10 px-1.5 text-[11px] font-medium text-warning',
              'transition-colors hover:bg-warning/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:pointer-events-none disabled:opacity-50',
              className,
            )}
          >
            <AlertTriangle className="h-3 w-3 shrink-0" />
            <span>{rule.shortLabel}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{aria}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
